import {
  backButton,
  closingBehavior,
  init as initSDK,
  initData,
  isRequestingWriteAccess,
  mainButton,
  miniApp,
  requestWriteAccess,
  retrieveLaunchParams,
  setDebug,
  setMiniAppBackgroundColor,
  setMiniAppBottomBarColor,
  setMiniAppHeaderColor,
  swipeBehavior,
  themeParams,
  useSignal,
  viewport,
} from "@telegram-apps/sdk-react"
import { PropsWithChildren, useEffect } from "react"
import { useClientOnce } from "@hooks/useClientOnce"

export default function TMALoader(props: Readonly<PropsWithChildren>) {
  const debug = import.meta.env.NODE_ENV === "development"

  useClientOnce(() => {
    initSDK()

    initData.restore()

    if (!themeParams.isMounted()) themeParams.mountSync()
    if (!miniApp.isMounted() && miniApp.isSupported()) miniApp.mountSync()
    if (!closingBehavior.isMounted() && closingBehavior.mount.isAvailable())
      closingBehavior.mount()
    if (
      !viewport.isMounted() &&
      !viewport.isMounting() &&
      viewport.mount.isAvailable()
    )
      viewport.mount()
    if (!swipeBehavior.isMounted() && swipeBehavior.mount.isAvailable())
      swipeBehavior.mount()
    if (
      backButton.isSupported() &&
      !backButton.isMounted() &&
      backButton.mount.isAvailable()
    )
      backButton.mount()
    if (!mainButton.isMounted() && mainButton.mount.isAvailable())
      mainButton.mount()
  })

  useEffect(() => {
    setDebug(debug)
    if (debug) {
      void import("eruda").then((lib) => lib.default.init())
    }
  }, [debug])

  const isThemeParamsMounted = useSignal(themeParams.isMounted)
  useEffect(() => {
    if (isThemeParamsMounted && !themeParams.isCssVarsBound()) {
      themeParams.bindCssVars()
    }
  }, [isThemeParamsMounted])

  const isMiniAppMounted = useSignal(miniApp.isMounted)
  useEffect(() => {
    if (isMiniAppMounted && !miniApp.isCssVarsBound()) {
      miniApp.bindCssVars()
    }
  }, [isMiniAppMounted])

  const isClosingBehaviorMounted = useSignal(closingBehavior.isMounted)
  useEffect(() => {
    if (isClosingBehaviorMounted && !closingBehavior.isConfirmationEnabled()) {
      closingBehavior.enableConfirmation()
    }
  }, [isClosingBehaviorMounted])

  useEffect(() => {
    if (!isRequestingWriteAccess()) {
      void requestWriteAccess()
    }
  }, [])

  const isViewportMounted = useSignal(viewport.isMounted)
  useEffect(() => {
    if (isViewportMounted) {
      if (!viewport.isCssVarsBound()) {
        viewport.bindCssVars()
      }

      if (!viewport.isExpanded() && viewport.expand.isAvailable()) {
        viewport.expand()
      }

      if (
        !viewport.isFullscreen() &&
        !viewport.changeFullscreenPromise() &&
        viewport.requestFullscreen.isAvailable() &&
        ["ios", "android", "android_x"].includes(
          String(retrieveLaunchParams().tgWebAppPlatform)
        )
      ) {
        viewport.requestFullscreen()
      }
    }
  }, [isViewportMounted])

  const isSwipeBehaviorMounted = useSignal(swipeBehavior.isMounted)
  useEffect(() => {
    if (
      isSwipeBehaviorMounted &&
      swipeBehavior.isVerticalEnabled() &&
      swipeBehavior.disableVertical.isAvailable()
    ) {
      swipeBehavior.disableVertical()
    }
  }, [isSwipeBehaviorMounted])

  const isTmaDark = useSignal(themeParams.isDark)
  const isSystemDark = globalThis.matchMedia(
    "(prefers-color-scheme: dark)"
  ).matches

  useEffect(() => {
    const setTheme = () => {
      const isDark = isThemeParamsMounted ? isTmaDark : isSystemDark

      const color = isDark ? "#171717" : "#F4F4F4"
      const footerColor = isDark ? "#1E1E1E" : "#FFFFFF"

      if (setMiniAppBackgroundColor.isAvailable()) {
        setMiniAppBackgroundColor(color)
      }
      if (setMiniAppHeaderColor.isAvailable()) setMiniAppHeaderColor(color)
      if (setMiniAppBottomBarColor.isAvailable()) {
        setMiniAppBottomBarColor(footerColor)
      }

      if (isDark) {
        document.documentElement.classList.add("dark")
      } else {
        document.documentElement.classList.remove("dark")
      }
    }

    setTheme()

    const mediaQuery = globalThis.matchMedia("(prefers-color-scheme: dark)")
    mediaQuery.addEventListener("change", setTheme)
    return () => mediaQuery.removeEventListener("change", setTheme)
  }, [isThemeParamsMounted, isTmaDark, isSystemDark])

  return <div className="safe-area">{props.children}</div>
}
